import React, { useEffect, useState, useMemo } from 'react';
import { useUser } from '../../context/UserContext';
import useIsMobile from '../../utils/useIsMobile';
import CustomSnackbar from '../../utils/customsnackbar';
import MainView from '../views/MainView';
import useCreateCustomChannelPointsReward from '../../utils/useCreateCustomChannelPointsReward';

import { Toolbar } from 'primereact/toolbar';
import { ToggleButton } from 'primereact/togglebutton';
import { IconField } from 'primereact/iconfield';
import { InputText } from 'primereact/inputtext';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';

import { Box, Card, CardActionArea, Grid } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * ViewerRewards – list & manage custom channel-point rewards.
 *
 * @param {object} props
 * @param {Function} [props.openDialogComponent]
 * @param {Function} [props.setIsBlur]
 * @param {string}   [props.backgroundColor]
 * @param {object}   [props.userSettings]
 */
const ViewerRewards = ({openDialogComponent = () => {}, setIsBlur = () => {}, backgroundColor = '#5D3B9E', userSettings = {}}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const authCtx = useUser();
    const isMobile = useIsMobile();
    const {createReward, errorText, isLoading} = useCreateCustomChannelPointsReward();

    /* ─────────────────────────── state ─────────────────────────── */
    const [loading, setLoading] = useState(true);
    const [allRewards, setAllRewards] = useState([]);
    const [manageableRewards, setManageableRewards] = useState([]);
    const [showDisabled, setShowDisabled] = useState(true);
    const [inputRequired, setInputRequired] = useState(false);
    const [manageableRequired, setManageableRequired] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [snackAlert, setSnackAlert] = useState({open: false, severity: 'success', snackText: '', autoHideDuration: 3000});
    const [missingPermissions, setMissingPermissions] = useState(false);

    /* ───────────────────────── effects ────────────────────────── */
    useEffect(() => {
        if (!allRewards.length) {
            const perms = [...(userSettings.twitchPermissions ?? [])];
            if (perms.includes('channel:read:redemptions') || perms.includes('channel:manage:redemptions')) {
                retrieveRewards();
            } else {
                setLoading(false);
                setMissingPermissions(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (errorText !== '') {
            setSnackAlert(s => ({ ...s, open: true, severity: 'error', snackText: `Error creating reward: ${errorText}` }));
        }
    }, [errorText]);
    

    /** Fetch rewards from backend. */
    const retrieveRewards = async () => {
        try {
            const url = `${apiUrl}/user/viewerrewards?token=${authCtx.token}`;
            const res = await (await fetch(url)).json();
            setAllRewards(res.allRewards ?? []);
            setManageableRewards(res.manageableRewards ?? []);
        } catch (err) {
            console.error(err);
            setSnackAlert(s => ({ ...s, open: true, severity: 'error', snackText: 'Error retrieving rewards' }));
        } finally {
            setLoading(false);
        }
    };

    /* ──────────────────────── helpers ─────────────────────────── */

    /** Truncate long strings. */
    const truncate = (text = '', max = 50) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

    /** HEX → rgba(). */
    const hexToRGBA = (hex = '#000000', alpha = 0.1) => {
        const clean = hex.replace('#', '').padStart(6, '0');
        const [r, g, b] = [0, 2, 4].map(i => parseInt(clean.slice(i, i + 2), 16) || 0);
        return `rgba(${r},${g},${b},${alpha})`;
    };

    /** Fast lookup of manageable IDs. */
    const manageableIds = useMemo(() => new Set(manageableRewards.filter(r => r?.id != null).map(r => String(r.id))), [manageableRewards]);

    /** Rewards after toggles + search. */
    const displayedRewards = useMemo(() => {
        let base = showDisabled ? allRewards : allRewards.filter(r => r?.is_enabled);
        if (inputRequired) base = base.filter(r => r?.is_user_input_required);
        if (manageableRequired) base = base.filter(r => manageableIds.has(String(r?.id)));
        if (!searchQuery.trim()) return base;
        const q = searchQuery.toLowerCase();
        return base.filter(r => (r?.title ?? '').toLowerCase().includes(q) || (r?.prompt ?? '').toLowerCase().includes(q));
    }, [allRewards, showDisabled, inputRequired, manageableRequired, manageableIds, searchQuery]);

    /* ────────────────────── reward card ───────────────────────── */
    /**
     * @param {{ reward: import('@twitchapis/channel-points').CustomReward, manageable:boolean }} props
     */
    const RewardItem = ({ reward = {}, manageable = false }) => {
        const { title = '', background_color = '#000000', is_enabled = false, cost = 0, prompt = '', id = '' } = reward;
        // Local hover state so we know when to show the delete button
        const [isHover, setIsHover] = useState(false);
        const [isDeleting, setIsDeleting] = useState(false);
        const styleCard = {
            height: 150,
            background: `radial-gradient(circle, ${hexToRGBA(background_color, 0.1)} 0%, ${hexToRGBA(background_color,0.05)} 100%)`,
            border: `2px solid ${hexToRGBA(background_color, 0.3)}`,
            filter: is_enabled ? 'none' : 'grayscale(1) opacity(0.6)',
            transition: 'filter .25s'
        };
        const styleAction = {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            pointerEvents: is_enabled ? 'auto' : 'none'
        };
        const handleClick = () => is_enabled && openDialogComponent('updatereward', { ...reward, manageable }, retrieveRewards);
        const handleDelete = async (e) => {
            setIsDeleting(true);
            e.stopPropagation(); // Don't trigger the normal card click
            const url = `${apiUrl}/user/customreward?token=${authCtx.token}&id=${id}`;
            const options = {
                method: 'DELETE'
            };
            try {
                const response = await fetch(url, options);
                if (response.ok) {
                    await retrieveRewards();
                } else {
                    setSnackAlert(s => ({ ...s, open: true, severity: 'error', snackText: `Error deleting reward: ${response.statusText}` }));
                } 
            } catch (error) {
                setSnackAlert(s => ({ ...s, open: true, severity: 'error', snackText: `Error deleting reward: ${error.message}` }));
            } finally {
                setIsDeleting(false);
            }
        };

        return (
            <Grid item xs={2}>
                <motion.div whileHover={!isMobile && { scale: [1, 1.025, 1] }} whileTap={!isMobile && { scale: [1, 0.95, 1] }}
                    onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
                    <Card style={styleCard}>
                        {/* ❶  Wrapper is the positioning context */}
                        <Box sx={{position: 'relative'}}>
                            {/* ❷  Delete button (hover-only via CSS) */}
                            {manageable && isHover && (
                                <Button severity='danger' onClick={handleDelete} icon='pi pi-times' size='small'
                                    style={{margin: '0px', padding: '2px', width: '50px', position: 'absolute', top: 8, right: 8, zIndex: 2}} />
                            )}
                        </Box>
                        {isDeleting ? (
                            <h2 style={{ margin: '16px 16px 4px' }}>Deleting...</h2>
                        ) : (
                        <CardActionArea disableRipple disableTouchRipple sx={styleAction} onClick={handleClick}>
                            <h3 style={{ margin: '16px 16px 4px' }}>{title}</h3>
                            <p style={{ margin: '8px 16px' }}>{`Cost: ${cost}`}</p>
                            <p style={{ margin: '0 16px' }}>{truncate(prompt, 60)}</p>
                            <p style={{ margin: '8px 16px' }}>{`Can be edited here: ${manageable ? 'Yes' : 'No'}`}</p>
                        </CardActionArea>
                        )}
                    </Card>
                </motion.div>
            </Grid>
        );
    };

    /* ───────────── “Add reward” tile ───────────── */
    const AddRewardItem = () => {
        const styleCard = {
            height: 150,
            border: '2px dashed #777',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
        };
        const handleClick = () => !isLoading && createReward({title: 'New Reward', cost: 100, prompt: '', cb: retrieveRewards});
        return (
            <Grid item xs={2}>
                <motion.div whileHover={!isMobile && { scale: [1, 1.05, 1] }}>
                    <Card style={styleCard} onClick={handleClick}>
                        {isLoading ? (
                            <h2>Creating reward...</h2>
                        ) : (
                            <h2 style={{ fontSize: 48, margin: 0 }}>+</h2>
                        )}
                    </Card>
                </motion.div>
            </Grid>
        );
    };

    /* ───────────────────────── toolbar UI ───────────────────────── */
    const leftToolbar = (
        <div>
            <ToggleButton checked={showDisabled} onChange={e => setShowDisabled(e.value)} onLabel="Disabled Rewards" offLabel="Disabled Rewards"
                onIcon="pi pi-eye" offIcon="pi pi-eye-slash" className="p-button-sm w-full" />
            <ToggleButton checked={inputRequired} onChange={e => setInputRequired(e.value)} onLabel="Rewards Without Text Input" offLabel="Rewards Without Text Input"
                onIcon="pi pi-eye-slash" offIcon="pi pi-eye" className="p-button-sm w-full" />
            <ToggleButton checked={manageableRequired} onChange={e => setManageableRequired(e.value)} onLabel="Not Created By Ratbot" offLabel="Not Created By Ratbot"
                onIcon="pi pi-eye-slash" offIcon="pi pi-eye" className="p-button-sm w-full" />
        </div>
    );

    const rightToolbar = (
        <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search" />
        </IconField>
    );

    /* ─────────────────────────── render ─────────────────────────── */
    return (
        <MainView allowScroll>
            <CustomSnackbar {...snackAlert} setSnackAlert={setSnackAlert} />
            {missingPermissions ? (
                <Grid container>
                    <Grid item xs={12}>
                        <h2>Ratbot does not have permission to read or manage rewards</h2>
                    </Grid>
                    <Grid item xs={12}>
                        <Button style={{color: 'black'}} severity='success' label='Authorize Access (Twitch)' 
                            onClick={() => openDialogComponent('addtwitchpermission', {scope: ['channel:read:redemptions', 'channel:manage:redemptions']})} />
                    </Grid>
                </Grid>
            ) : (
                <React.Fragment>
                    {(loading) ? (
                        <h2>Retrieving channel point rewards from Twitch...</h2>
                    ) : (
                        <React.Fragment>
                            <Toolbar start={leftToolbar} end={rightToolbar} style={{ marginBottom: '16px', width: '100%' }} />
                                <Grid container spacing={2}>
                                    {displayedRewards.map(rw => (
                                        <RewardItem key={rw?.id ?? rw?.title} reward={rw} manageable={manageableIds.has(String(rw?.id))} />
                                    ))}
                                    <AddRewardItem />
                                </Grid>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </MainView>
    );
};

export default ViewerRewards;